import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Info, Users } from 'lucide-react';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useSession } from 'session-context';

interface RoleAction {
  role: string;
  name: string;
  cost: number;
  description: string;
  checkType: string;
}

interface DailyActivity {
  name: string;
  description: string;
}

interface UserActivityData {
  totalCAP: number;
  remainingCAP: number;
  spentActivities: Record<string, number>;
  dailyActivities: Record<string, string[]>;
}

interface CAPTrackerProps {
  sharedDaysInTurn: number;
  userActivities: Record<string, UserActivityData>;
  onUserActivityChange: (userId: string, data: UserActivityData) => void;
  onSharedDaysInTurnChange: (days: number) => void;
}

// Role Actions that cost CAP
const roleActions: RoleAction[] = [
  { 
    role: 'Captain', 
    name: 'Leadership', 
    cost: 1, 
    description: 'Grant a +2 competence bonus on all crew Profession(Sailor) checks. +1 for every 5 points you exceed the DC.', 
    checkType: 'Captain check'
  },
  { 
    role: 'First Mate', 
    name: 'Morale Boost', 
    cost: 1, 
    description: 'Grant a +1 morale bonus on all crew skill checks. +1 for every 10 points you exceed the DC.', 
    checkType: 'First Mate check'
  },
  { 
    role: 'Pilot', 
    name: 'Dead Reckoning', 
    cost: 1, 
    description: 'Grant a +2 insight bonus on your next Pilot check. +1 for every 10 points you exceed the DC.', 
    checkType: 'Pilot check'
  },
  { 
    role: 'Mage', 
    name: 'Arcane Augmentation', 
    cost: 1, 
    description: 'Grant a +1 enhancement bonus to the ship\'s hardness.', 
    checkType: 'Mage check'
  },
  { 
    role: 'Prelate', 
    name: 'Safe Harbor', 
    cost: 1, 
    description: 'Have one crew member you can see reroll a failed skill check.', 
    checkType: 'Prelate check'
  },
  { 
    role: 'Windsinger', 
    name: 'Shape the Winds', 
    cost: 1, 
    description: 'Increase the base sailing speed of the vessel by 5ft (enhancement bonus) (½ mph).', 
    checkType: 'Windsinger check'
  },
  { 
    role: 'Artillerist', 
    name: 'Weapon Maintenance', 
    cost: 1, 
    description: 'Gain a +1 competence bonus to attack rolls with ship weapons for the crew. +1 for every 10 points you exceed the DC.', 
    checkType: 'Artillerist check'
  },
  { 
    role: 'Boatswain', 
    name: 'Inspect Rigging', 
    cost: 1, 
    description: 'Grant a +2 circumstance bonus on all crew Balance and Climb checks while using ropes. +1 for every 5 points you exceed the DC.', 
    checkType: 'Boatswain check'
  },
  { 
    role: 'Cook', 
    name: 'Prepare Hearty Meal', 
    cost: 1, 
    description: 'Grant +2 temporary HP to the crew. +1 for every 10 points you exceed the DC. (Cannot be used in combat)', 
    checkType: 'Cook check'
  },
  { 
    role: 'Conning Officer', 
    name: 'Handle the Ship', 
    cost: 1, 
    description: 'Have the helmsman roll twice on their next ship handling check and take the higher result.', 
    checkType: 'Conning Officer check'
  },
  { 
    role: 'Master-at-Arms', 
    name: 'Combat Drills', 
    cost: 1, 
    description: 'Grant a +1 competence bonus on weapon attack rolls for the crew while on the ship deck. +1 for every 10 points you exceed the DC.', 
    checkType: 'Master-at-Arms check'
  },
  { 
    role: 'Surgeon', 
    name: 'Preventive Care', 
    cost: 1, 
    description: 'Grant a +1 circumstance bonus on Constitution saves against poisons and diseases for the crew. +1 for every 10 points you exceed the DC.', 
    checkType: 'Surgeon check'
  }
];

// Daily Activities (one per day, no CAP cost)
const dailyActivityOptions: DailyActivity[] = [
  { name: 'Repairs', description: 'Repair 5 HP to a damaged hull section or shore a weakened section' },
  { name: 'Scouting', description: 'Lower the chances of a hostile encounter by 5%' },
  { name: 'Fishing/Foraging', description: 'Take 20 on a Survival check for foraging food' },
  { name: 'Carousing', description: 'Attempt to increase Crew Morale' },
  { name: 'Guard Duty', description: 'Choose a time period. Actively alert for potential dangers then' },
  { name: 'Cleaning', description: 'Attempt to increase Cleanliness' },
  { name: 'Crafting', description: 'Spend time crafting' },
  { name: 'Training/Retraining', description: 'Spend time training, researching, or retraining' },
  { name: 'Gambling', description: 'Gamble amongst the crew for a chance to win gold or items' },
  { name: 'Storytelling', description: 'Regale the crew with tales to increase Fame or Infamy' },
];

// Group role actions by role
const roleGroups = roleActions.reduce((groups, action) => {
  if (!groups[action.role]) {
    groups[action.role] = [];
  }
  groups[action.role].push(action);
  return groups;
}, {} as Record<string, RoleAction[]>);

const CAPTracker: React.FC<CAPTrackerProps> = ({
  sharedDaysInTurn,
  userActivities,
  onUserActivityChange,
  onSharedDaysInTurnChange
}) => {
  const { userId, activeUsers } = useSession();
  const [selectedActivity, setSelectedActivity] = useState<string>("");
  const [expandedRole, setExpandedRole] = useState<string | null>(null);
  const [showUsersActivities, setShowUsersActivities] = useState(false);

  // Get current user's activity data or create default if not exists
  const defaultUserData = {
    totalCAP: 1,
    remainingCAP: 1,
    spentActivities: {},
    dailyActivities: {}
  };
  
  // Make sure we have a valid userActivities object
  const safeUserActivities = userActivities || {};
  
  // Get current user data with defaults to prevent undefined
  const currentUserData = safeUserActivities[userId] || defaultUserData;
  
  // Ensure dailyActivities always exists
  const safeDailyActivities = currentUserData.dailyActivities || {};
  
  // Create days array
  const days = Array.from({ length: sharedDaysInTurn }, (_, i) => `Day ${i + 1}`);
  
  // Initialize selectedDay state after ensuring days are available
  const [selectedDay, setSelectedDay] = useState<string>(days[0] || 'Day 1');

  // Initialize days in turn for current user if not exists
  useEffect(() => {
    // Create a default daily activities object if it doesn't exist or is empty
    if (!currentUserData.dailyActivities || Object.keys(currentUserData.dailyActivities || {}).length === 0) {
      const newDailyActivities: Record<string, string[]> = {};
      for (let i = 1; i <= sharedDaysInTurn; i++) {
        newDailyActivities[`Day ${i}`] = [];
      }
      
      updateUserData({
        ...currentUserData,
        dailyActivities: newDailyActivities
      });
    }
  }, [sharedDaysInTurn]);

  // Helper function to update user data
  const updateUserData = (newData: UserActivityData) => {
    onUserActivityChange(userId, newData);
  };

  const handleSpendCAP = (action: RoleAction) => {
    if (currentUserData.remainingCAP >= action.cost) {
      const newRemainingCAP = currentUserData.remainingCAP - action.cost;
      const actionKey = `${action.role}: ${action.name}`;
      const newSpentActivities = {
        ...(currentUserData.spentActivities || {}),
        [actionKey]: ((currentUserData.spentActivities || {})[actionKey] || 0) + 1,
      };
      
      updateUserData({
        ...currentUserData,
        remainingCAP: newRemainingCAP,
        spentActivities: newSpentActivities
      });
    }
  };

  const handleResetCAP = () => {
    updateUserData({
      ...currentUserData,
      remainingCAP: currentUserData.totalCAP,
      spentActivities: {}
    });
  };

  const handleChangeTotalCAP = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTotal = parseInt(e.target.value) || 1;
    updateUserData({
      ...currentUserData,
      totalCAP: newTotal,
      remainingCAP: newTotal,
      spentActivities: {}
    });
  };

  const handleChangeDaysInTurn = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDays = parseInt(e.target.value) || 1;
    onSharedDaysInTurnChange(newDays);
    
    // Update all users' daily activities to match the new day count
    const newDailyActivities: Record<string, string[]> = { ...(safeDailyActivities) };
    
    // Create new days if needed
    for (let i = 1; i <= newDays; i++) {
      const dayKey = `Day ${i}`;
      if (!newDailyActivities[dayKey]) {
        newDailyActivities[dayKey] = [];
      }
    }
    
    // Remove days that exceed the new count
    Object.keys(newDailyActivities).forEach(day => {
      const dayNumber = parseInt(day.split(" ")[1]);
      if (dayNumber > newDays) {
        delete newDailyActivities[day];
      }
    });
    
    updateUserData({
      ...currentUserData,
      dailyActivities: newDailyActivities
    });
  };

  const handleSelectDailyActivity = () => {
    if (!selectedActivity) return;
    
    const newDailyActivities: Record<string, string[]> = { ...(safeDailyActivities) };
    
    // Remove any existing activity if one is already assigned
    if (newDailyActivities[selectedDay]?.length > 0) {
      newDailyActivities[selectedDay] = [];
    } else if (!newDailyActivities[selectedDay]) {
      // Initialize if it doesn't exist
      newDailyActivities[selectedDay] = [];
    }
    
    // Add the new activity
    newDailyActivities[selectedDay] = [selectedActivity];
    
    updateUserData({
      ...currentUserData,
      dailyActivities: newDailyActivities
    });
    
    setSelectedActivity("");
  };

  // Get all users' selections for display
  const getUserActivitiesSummary = () => {
    if (!safeUserActivities) return [];
    
    return Object.entries(safeUserActivities).map(([id, data]) => {
      if (!data) return { id, name: 'Unknown User', totalCAP: 0, remainingCAP: 0, spentActivities: {}, dailyActivities: {} };
      
      const userName = activeUsers[id]?.name || 'Unknown User';
      return {
        id,
        name: userName,
        totalCAP: data.totalCAP || 0,
        remainingCAP: data.remainingCAP || 0,
        spentActivities: data.spentActivities || {},
        dailyActivities: data.dailyActivities || {}
      };
    });
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Ship Activity Tracker</CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowUsersActivities(!showUsersActivities)}
            className="flex items-center gap-1"
          >
            <Users className="h-4 w-4" />
            <span className="ml-1">Crew Activities</span>
          </Button>
        </div>
        <CardDescription>Manage personal Crew Action Points (CAP) and Daily Activities</CardDescription>
      </CardHeader>
      <CardContent>
        {showUsersActivities && (
          <div className="mb-6 p-4 border rounded-md bg-muted">
            <h3 className="text-lg font-semibold mb-2">Crew Members' Activities</h3>
            <Accordion type="single" collapsible className="w-full">
              {getUserActivitiesSummary().map((user) => (
                <AccordionItem key={user.id} value={user.id}>
                  <AccordionTrigger className="text-sm">
                    {user.name} - {user.remainingCAP}/{user.totalCAP} CAP
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="text-sm space-y-2">
                      <div>
                        <h4 className="font-medium">Spent CAP:</h4>
                        {Object.entries(user.spentActivities || {}).length > 0 ? (
                          <ul className="list-disc pl-5">
                            {Object.entries(user.spentActivities || {}).map(([activity, count]) => (
                              <li key={activity}>
                                {activity}: {count} time{count !== 1 ? 's' : ''}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-muted-foreground">No CAP spent</p>
                        )}
                      </div>
                      
                      <div>
                        <h4 className="font-medium">Daily Activities:</h4>
                        {user.dailyActivities && Object.entries(user.dailyActivities).length > 0 ? (
                          <ul className="list-disc pl-5">
                            {Object.entries(user.dailyActivities || {})
                              .filter(([_, activities]) => activities && activities.length > 0)
                              .map(([day, activities]) => (
                                <li key={day}>
                                  {day}: {activities.join(', ')}
                                </li>
                              ))}
                          </ul>
                        ) : (
                          <p className="text-muted-foreground">No activities planned</p>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}
        
        <Tabs defaultValue="cap" className="w-full">
          <TabsList className="grid grid-cols-2 mb-4">
            <TabsTrigger value="cap">Role Actions (CAP)</TabsTrigger>
            <TabsTrigger value="daily">Daily Activities</TabsTrigger>
          </TabsList>
          
          <TabsContent value="cap" className="space-y-4">
            <div className="flex items-center space-x-2">
              <label htmlFor="totalCAP">Your Total CAP:</label>
              <Input
                id="totalCAP"
                type="number"
                value={currentUserData.totalCAP}
                onChange={handleChangeTotalCAP}
                className="w-20"
              />
            </div>
            <div>Your Remaining CAP: {currentUserData.remainingCAP}</div>
            
            <div className="space-y-4">
              {Object.entries(roleGroups).map(([role, actions]) => (
                <div key={role} className="border rounded-md overflow-hidden">
                  <div 
                    className="bg-muted p-2 font-medium cursor-pointer flex justify-between items-center"
                    onClick={() => setExpandedRole(expandedRole === role ? null : role)}
                  >
                    <span>{role}</span>
                    <span>{expandedRole === role ? '▲' : '▼'}</span>
                  </div>
                  
                  {expandedRole === role && (
                    <div className="p-2 space-y-2">
                      {actions.map((action) => (
                        <div key={action.name} className="space-y-1">
                          <div className="flex justify-between items-start">
                            <div className="flex items-center">
                              <span className="font-medium">{action.name}</span>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-6 w-6 ml-1">
                                      <Info className="h-3 w-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p><strong>Roll a {action.checkType}</strong></p>
                                    <p>{action.description}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <Badge>{action.cost} CAP</Badge>
                          </div>
                          <Button 
                            onClick={() => handleSpendCAP(action)}
                            disabled={currentUserData.remainingCAP < action.cost}
                            size="sm"
                            className="w-full"
                          >
                            Use {action.name}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div>
              <h3 className="font-bold mt-4">Your Spent Activities:</h3>
              {currentUserData.spentActivities && Object.keys(currentUserData.spentActivities).length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {Object.entries(currentUserData.spentActivities || {}).map(([actionKey, count]) => (
                    <li key={actionKey} className="flex justify-between text-sm">
                      <span>{actionKey}:</span> 
                      <span>{count} time{count !== 1 ? 's' : ''}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground mt-2">No CAP spent yet</p>
              )}
            </div>
            <Button onClick={handleResetCAP} className="w-full">Reset Your CAP</Button>
          </TabsContent>
          
          <TabsContent value="daily" className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <label htmlFor="daysInTurn">Days in turn (shared):</label>
              <Input
                id="daysInTurn"
                type="number"
                min="1"
                max="10"
                value={sharedDaysInTurn}
                onChange={handleChangeDaysInTurn}
                className="w-20"
              />
            </div>
            
            <div className="flex flex-wrap gap-2 mb-4">
              {days.map((day) => (
                <Button 
                  key={day} 
                  variant={selectedDay === day ? "default" : "outline"}
                  onClick={() => setSelectedDay(day)}
                  className="px-3 py-1"
                >
                  {day}
                </Button>
              ))}
            </div>
            
            <div className="p-4 bg-muted rounded-md">
              <h3 className="font-medium mb-2">Your Activity for {selectedDay}:</h3>
              {safeDailyActivities[selectedDay]?.length > 0 ? (
                <div>
                  <p className="font-bold">{safeDailyActivities[selectedDay][0]}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {dailyActivityOptions.find(a => a.name === safeDailyActivities[selectedDay][0])?.description}
                  </p>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    className="mt-2"
                    onClick={() => {
                      const newDailyActivities = { ...safeDailyActivities };
                      newDailyActivities[selectedDay] = [];
                      updateUserData({
                        ...currentUserData,
                        dailyActivities: newDailyActivities
                      });
                    }}
                  >
                    Remove Activity
                  </Button>
                </div>
              ) : (
                <p className="text-muted-foreground">No activity selected</p>
              )}
            </div>
            
            <div className="mt-4">
              <RadioGroup value={selectedActivity} onValueChange={setSelectedActivity} className="gap-2">
                {dailyActivityOptions.map((activity) => (
                  <div key={activity.name} className="flex items-start space-x-2 p-2 border rounded-md">
                    <RadioGroupItem value={activity.name} id={`activity-${activity.name}`} />
                    <div className="grid gap-1.5">
                      <Label htmlFor={`activity-${activity.name}`} className="font-medium">
                        {activity.name}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {activity.description}
                      </p>
                    </div>
                  </div>
                ))}
              </RadioGroup>
            </div>
            
            <Button 
              onClick={handleSelectDailyActivity} 
              disabled={!selectedActivity}
              className="w-full mt-4"
            >
              Assign Activity to {selectedDay}
            </Button>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default CAPTracker;